import React, { useEffect, useState } from "react";
import {
  Card,
  Modal,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Select,
  Table,
  Space,
  Tooltip,
  message,
  Popconfirm,
} from "antd";
import dayjs from "dayjs";
import {
  PlusOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  QrcodeOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axiosInstance, { baseURL } from "../../../configs"; // sesuaikan path
import { getUser } from "../../../utils/DataUser";
import { canCreate, canEdit, canDelete, MODULES } from "../../../utils/rbac";

const { Title, Text } = Typography;


const defaultPageSize = 20;

export default function BrowseItem() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  // filter
  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);

  // Generate QR state
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrMode, setQrMode] = useState("all"); // "all" | "nopen"
  const [qrNopen, setQrNopen] = useState("");

  const [deletingId, setDeletingId] = useState(null);

  const apiBase = process.env.REACT_APP_PDF_URL || "/api";
  console.log("API Base URL:", apiBase);
  const openPDF = (id_stripping) => {
    const url = `${apiBase}/stripping/${id_stripping}/tally-pdf?t=${Date.now()}`;
    setPdfUrl(url);
    setShowPdfModal(true);
  };

  const fetchItems = async (page = 1, pageSize = defaultPageSize) => {
    setLoading(true);
    try {
      const user = getUser();

      // Build params - untuk user level 7, tambahkan filter id_customer
      const params = {
        page,
        limit: pageSize,
        customer_name: searchName || undefined,
        document_number: searchCode || undefined,
      };

      // Filter di backend untuk user level 7 (Importir)
      if (user && (user.id_user_level === 7 || user.id_user_level === "7")) {
        params.id_customer = user.username; // username = NPWP
        console.log("DEBUG: Fetching with id_customer filter:", user.username);
      }

      const res = await axiosInstance.get("/stripping", { params });

      const raw = res.data || {};
      const payload = raw.data || raw;
      const rows =
        payload.items ||
        payload.rows ||
        payload.data ||
        (Array.isArray(payload) ? payload : []);

      // Baca total dari pagination response (backend mengembalikan { data, pagination: { total } })
      const total = raw.pagination?.total ||
        payload.total ||
        payload.count ||
        payload.pagination?.total ||
        rows.length;

      console.log("DEBUG: API Response - rows:", rows.length, "total:", total, "raw.pagination:", raw.pagination);

      setItems(rows);
      setPagination({
        current: page,
        pageSize,
        total: total || 0,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar pembongkaran barang");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, defaultPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchItems(1, pagination.pageSize);
  };

  const handleResetFilter = () => {
    setSearchName("");
    setSearchCode("");
    fetchItems(1, defaultPageSize);
  };

  const handleTableChange = (pager) => {
    fetchItems(pager.current, pager.pageSize);
  };

  const handleExportExcel = () => {
    window.open(`${baseURL}/stripping/export`, "_blank");
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id_stripping);
    try {
      await axiosInstance.delete(`/stripping/${record.id_stripping}`);
      message.success("Pembongkaran barang berhasil dihapus");
      // reload current page
      fetchItems(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error(err);
      message.error("Gagal menghapus pembongkaran barang");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD-MM-YYYY") : "-";



  const columns = [
    {
      title: "NO",
      dataIndex: "index",
      width: 70,
      align: "center",
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "NO PEMBONGKARAN",
      dataIndex: "stripping_number",
      width: 130,
    },
    {
      title: "TANGGAL PEMBONGKARAN",
      dataIndex: "stripping_date",
      width: 140,
      render: (value) => formatDate(value),
    },

    {
      title: "NO DOKUMEN",
      dataIndex: "document_number",
      width: 160,
      render: (value) => value || "-",
    },
    {
      title: "TGL DOKUMEN",
      dataIndex: "document_date",
      width: 140,
      render: (value) => value ? dayjs(value).format("DD-MM-YYYY") : "-",
    },

    {
      title: "TOTAL KEMASAN",
      dataIndex: "total_kemasan",
      width: 160,
      render: (value) => value || "-",
    },

    {
      title: "AKSI",
      key: "action",
      width: 160,
      align: "center",
      render: (_, record) => (
        <Space>
          {/* PRINT / VIEW PDF */}
          <Tooltip title="Lihat & Cetak PDF">
            <Button
              size="small"
              type="primary"
              icon={<PrinterOutlined />}
              style={{
                background: "#3699FF",
                borderColor: "#3699FF",
              }}
              onClick={() => openPDF(record.id_stripping)}
            />
          </Tooltip>

          {/* EDIT */}
          {canEdit(MODULES.INVENTORY) && (
            <Tooltip title={getUser().id_user_level === 7 || getUser().id_user_level === "7" ? "Lihat Detil" : "Edit"}>
              <Link to={`updatetallysheet/${record.id_stripping}`}>
                <Button
                  size="small"
                  type="primary"
                  icon={getUser().id_user_level === 7 || getUser().id_user_level === "7" ? <SearchOutlined /> : <EditOutlined />}
                  style={{ background: "#3699FF", borderColor: "#3699FF" }}
                />
              </Link>
            </Tooltip>
          )}

          {/* DELETE */}
          {canDelete(MODULES.INVENTORY) && getUser().id_user_level !== 7 && getUser().id_user_level !== "7" && (
            <Popconfirm
              title="Hapus Pembongkaran Barang"
              description={`Yakin ingin menghapus "${record.stripping_number}"?`}
              okText="Ya, Hapus"
              cancelText="Batal"
              onConfirm={() => handleDelete(record)}
              okButtonProps={{
                danger: true,
                loading: deletingId === record.id_stripping,
              }}
            >
              <Tooltip title="Hapus">
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={deletingId === record.id_stripping}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Browse Pembongkaran Barang
          </Title>
          <Text type="secondary">
            Kelola daftar Pembongkaran Barang untuk kebutuhan inventory &
            kepabeanan.
          </Text>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Space wrap>
            {canCreate(MODULES.INVENTORY) && getUser().id_user_level !== 7 && getUser().id_user_level !== "7" && (
              <Link to="/inventory/tallying">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{
                    background: "#1BC5BD",
                    borderColor: "#1BC5BD",
                    fontWeight: 600,
                  }}
                >
                  Tambah
                </Button>
              </Link>
            )}
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{
                background: "#1E1EFA",
                borderColor: "#1E1EFA",
                fontWeight: 600,
              }}
            >
              Excel
            </Button>
            <Button
              type="primary"
              icon={<QrcodeOutlined />}
              onClick={() => { setQrMode("all"); setQrNopen(""); setShowQrModal(true); }}
              style={{
                background: "#8950FC",
                borderColor: "#8950FC",
                fontWeight: 600,
              }}
            >
              Generate QR
            </Button>
          </Space>
        </Col>
      </Row>

      {/* FILTER BAR */}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Text strong>Cari Berdasarkan Pemilik Barang</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari Item..."
            style={{ marginTop: 4 }}
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>

        <Col xs={24} md={12}>
          <Text strong>Cari Berdasarkan No Pendaftaran</Text>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cari kode..."
            style={{ marginTop: 4 }}
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>

        <Col xs={24} style={{ textAlign: "right" }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
              Reset
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              style={{
                background: "#8950FC",
                borderColor: "#8950FC",
              }}
            >
              Apply Filter
            </Button>
          </Space>
        </Col>
      </Row>
      <Modal
        title="Tally Sheet PDF"
        open={showPdfModal}
        onCancel={() => setShowPdfModal(false)}
        width={900}
        footer={null}
        bodyStyle={{ padding: 0 }}
      >
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            title="Tally Sheet"
            style={{
              width: "100%",
              height: "80vh",
              border: "none",
            }}
          />
        )}
      </Modal>

      {/* === Modal Generate QR === */}
      <Modal
        title="Generate QR Label Barang"
        open={showQrModal}
        onCancel={() => setShowQrModal(false)}
        width={440}
        footer={null}
        bodyStyle={{ padding: 20 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Pilih Scope Generate:</Text>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div
              onClick={() => setQrMode("all")}
              style={{
                flex: 1, border: `2px solid ${qrMode === "all" ? "#8950FC" : "#ddd"}`,
                borderRadius: 8, padding: '10px 14px', cursor: 'pointer', textAlign: 'center',
                background: qrMode === "all" ? "#f5f0ff" : "#fff", transition: 'all .2s',
              }}
            >
              <div style={{ fontSize: 22 }}>🌐</div>
              <Text strong>Semua NOPEN</Text>
              <div style={{ fontSize: 11, color: '#888' }}>Semua lot stok aktif</div>
            </div>
            <div
              onClick={() => setQrMode("nopen")}
              style={{
                flex: 1, border: `2px solid ${qrMode === "nopen" ? "#8950FC" : "#ddd"}`,
                borderRadius: 8, padding: '10px 14px', cursor: 'pointer', textAlign: 'center',
                background: qrMode === "nopen" ? "#f5f0ff" : "#fff", transition: 'all .2s',
              }}
            >
              <div style={{ fontSize: 22 }}>📄</div>
              <Text strong>NOPEN Tertentu</Text>
              <div style={{ fontSize: 11, color: '#888' }}>Pilih satu NOPEN</div>
            </div>
          </div>

          {qrMode === "nopen" && (
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>Pilih NOPEN:</Text>
              <select
                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #d9d9d9', fontSize: 14 }}
                value={qrNopen}
                onChange={(e) => setQrNopen(e.target.value)}
              >
                <option value="">-- Pilih NOPEN --</option>
                {(() => {
                  const map = {};
                  items.forEach(r => {
                    const key = `${r.document_number}|${r.document_date}`;
                    if (r.document_number && !map[key]) {
                      map[key] = {
                        nopen: r.document_number,
                        dateLabel: r.document_date ? dayjs(r.document_date).format("DD MMM YYYY") : '-',
                        customer: r.nama_customer || '-',
                      };
                    }
                  });
                  return Object.keys(map).sort().map(k => (
                    <option key={k} value={k}>
                      {map[k].nopen} | {map[k].dateLabel} | {map[k].customer}
                    </option>
                  ));
                })()}
              </select>
            </div>
          )}
        </div>

        <button
          onClick={() => {
            if (qrMode === "nopen" && !qrNopen) {
              message.warning("Pilih NOPEN terlebih dahulu");
              return;
            }
            const [nopen, docDate] = qrNopen.split('|');
            const nopenParam = qrMode === "all" ? "ALL" : nopen;
            const dateParam = (qrMode === "all" || !docDate) ? "" : `&document_date=${docDate}`;
            window.open(`${apiBase}/stripping/qr-print?nopen=${encodeURIComponent(nopenParam)}${dateParam}`, "_blank");
            setShowQrModal(false);
          }}
          style={{
            width: '100%', padding: '10px', background: '#8950FC', color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 'bold', cursor: 'pointer',
          }}
        >
          🖨 Generate &amp; Buka Halaman Cetak
        </button>
      </Modal>

      <Table
        style={{ marginTop: 36 }}
        rowKey="id_stripping"
        dataSource={items}
        columns={columns}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `Total ${total} data | ${range[0]}-${range[1]}`,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
}
